// src/views/CreateQuestion.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { questionsAPI, formationsAPI, plateformesAPI, domainesExpertiseAPI, learndashSyncAPI } from '../api/api';
import QuestionHistory from '../components/QuestionHistory';

// Modale de création de formation
const FormationModal = ({ show, onClose, onCreate, formData, setFormData }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer une nouvelle formation</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nom de la formation"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Description de la formation"
              rows="3"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
            >
              Annuler
            </button>
            <button
              onClick={onCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Créer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function CreateQuestionWithModals({ formations, domainesExpertise, onFormationCreated }) {
  const [formData, setFormData] = useState({
    intitule: '',
    feedback: '',
    type_reponse: 'QCU',
    statut: 'publié',  // Toutes les questions sont publiées par défaut
    donnees_dynamiques: false,  // Nouveau paramètre pour indiquer si la question est susceptible de changer fréquemment
    auto_sync_learndash: true,  // Synchronisation automatique avec LearnDash activée par défaut
    reponses: [{ contenu: '', est_correct: false }]
  });
  const [plateformes, setPlateformes] = useState([]);
  const [selectedPlateformes, setSelectedPlateformes] = useState([]);
  const [selectedFormations, setSelectedFormations] = useState([]);
  const [selectedDomainesExpertise, setSelectedDomainesExpertise] = useState([]);
  const [syncedPlatforms, setSyncedPlatforms] = useState([]); // Track synced platforms
  const [syncStatusDetails, setSyncStatusDetails] = useState(null); // Store full sync status details

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false); // For sync operation
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [showFormationModal, setShowFormationModal] = useState(false);
  const [newFormation, setNewFormation] = useState({ nom: '', description: '' });
  const navigate = useNavigate();
  const { id } = useParams(); // Pour l'édition

  // Charger les données de la question si en mode édition et charger les plateformes
  useEffect(() => {
    loadPlateformes();
    if (id) {
      loadQuestion();
      loadSyncStatus();
    }
  }, [id]);

  // Vérification des doublons avec debounce
  useEffect(() => {
    const intitule = formData.intitule.trim();
    if (intitule.length < 4) {
      setDuplicates([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await questionsAPI.checkDuplicate(intitule, id || null);
        setDuplicates(results);
      } catch {
        setDuplicates([]);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.intitule, id]);

  const loadPlateformes = async () => {
    try {
      const plateformesData = await plateformesAPI.getAll();
      setPlateformes(plateformesData);
    } catch (error) {
      console.error('Erreur lors du chargement des plateformes:', error);
    }
  };

  // Fonction pour charger le statut de synchronisation
  const loadSyncStatus = async () => {
    if (!id) return;
    try {
      const syncStatus = await learndashSyncAPI.getSyncStatus(id);
      if (syncStatus.isSynced) {
        setSyncedPlatforms(syncStatus.platforms.map(p => p.platform_instance));
        setSyncStatusDetails(syncStatus);
      } else {
        setSyncedPlatforms([]);
        setSyncStatusDetails(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du statut de synchronisation:', error);
      setSyncStatusDetails(null);
    }
  };

  // Function to view sync details with links
  const handleViewSyncDetails = () => {
    if (!syncStatusDetails || !syncStatusDetails.platforms || syncStatusDetails.platforms.length === 0) {
      alert('Cette question n\'est synchronisée avec aucune plateforme.');
      return;
    }

    const platformList = syncStatusDetails.platforms.map(p => {
      const questionLink = p.platform_question_id 
        ? `${p.platform_instance}/wp-admin/post.php?post=${p.platform_question_id}&action=edit`
        : p.platform_instance;
      return `Plateforme: ${p.platform_instance}
Statut: ${p.sync_status || 'synced'}
ID Question: ${p.platform_question_id || 'N/A'}
Lien: ${questionLink}`;
    }).join('\n\n');

    alert(`Question synchronisée avec ${syncStatusDetails.platforms.length} plateforme(s):\n\n${platformList}`);
  };

  // Fonction pour charger la question
  const loadQuestion = async () => {
    try {
      setLoading(true);
      const question = await questionsAPI.getById(id);
      setFormData({
        ...question,
        donnees_dynamiques: question.donnees_dynamiques || false,
        auto_sync_learndash: question.auto_sync_learndash !== false,  // Charger la valeur depuis la BDD
        reponses: question.reponses || [{ contenu: '', est_correct: false }]
      });

      // Charger les plateformes associées à la question
      try {
        const questionPlateformes = await plateformesAPI.getByQuestionId(id);
        const selectedIds = questionPlateformes.map(pf => pf.id);
        setSelectedPlateformes(selectedIds);
      } catch (error) {
        console.error('Erreur lors du chargement des plateformes de la question:', error);
        // Si la route n'existe pas encore, on initialise avec un tableau vide
        setSelectedPlateformes([]);
      }

      // Charger les formations et modules associés à la question
      if (question.formations && Array.isArray(question.formations)) {
        setSelectedFormations(question.formations.map(f => f.id));
      }
      if (question.domaines_expertise && Array.isArray(question.domaines_expertise)) {
        setSelectedDomainesExpertise(question.domaines_expertise.map(de => de.id));
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la question:', error);
      setError('Erreur lors du chargement de la question');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer la sélection d'une plateforme
  const handlePlateformeChange = (plateformeId) => {
    setSelectedPlateformes(prev => {
      if (prev.includes(plateformeId)) {
        return prev.filter(id => id !== plateformeId);
      } else {
        return [...prev, plateformeId];
      }
    });
  };

  // Fonction pour gérer la sélection d'une formation
  const handleFormationChange = (formationId) => {
    setSelectedFormations(prev => {
      if (prev.includes(formationId)) {
        return prev.filter(id => id !== formationId);
      } else {
        return [...prev, formationId];
      }
    });
  };

  // Fonction pour gérer la sélection d'un module
  const handleDomaineExpertiseChange = (domaineId) => {
    setSelectedDomainesExpertise(prev => {
      if (prev.includes(domaineId)) {
        return prev.filter(id => id !== domaineId);
      } else {
        return [...prev, domaineId];
      }
    });
  };

  // Fonction pour créer une nouvelle formation
  const createFormation = async () => {
    try {
      setLoading(true);
      const createdFormation = await formationsAPI.create(newFormation);

      // Appeler la fonction de callback pour informer le parent
      if (onFormationCreated) {
        onFormationCreated(createdFormation);
      }

      setShowFormationModal(false);
      setNewFormation({ nom: '', description: '' });
      // Sélectionner la nouvelle formation dans la liste
      setSelectedFormations(prev => [...prev, createdFormation.id]);
      alert('Formation créée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création de la formation:', error);
      if (error.message && error.message.includes('404')) {
        setError('La fonctionnalité de création de formations n\'est pas encore disponible dans le backend.');
      } else {
        setError('Erreur lors de la création de la formation: ' + (error.message || 'Erreur inconnue'));
      }
    } finally {
      setLoading(false);
    }
  };


  const addReponse = () => {
    setFormData({
      ...formData,
      reponses: [...formData.reponses, { contenu: '', est_correct: false }]
    });
  };

  const updateReponse = (index, field, value) => {
    const newReponses = [...formData.reponses];
    newReponses[index][field] = value;
    setFormData({ ...formData, reponses: newReponses });
  };

  const removeReponse = (index) => {
    if (formData.reponses.length > 1) {
      const newReponses = formData.reponses.filter((_, i) => i !== index);
      setFormData({ ...formData, reponses: newReponses });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation : Vérifier qu'au moins une formation et un module sont sélectionnés
    if (selectedFormations.length === 0) {
      setError('Veuillez sélectionner au moins une formation.');
      setLoading(false);
      return;
    }

    if (selectedDomainesExpertise.length === 0) {
      setError('Veuillez sélectionner au moins un module.');
      setLoading(false);
      return;
    }

    try {
      // Ajouter les formations, modules et plateformes sélectionnées aux données du formulaire
      const formDataWithAssociations = {
        ...formData,
        formation_ids: selectedFormations,
        domaine_expertise_ids: selectedDomainesExpertise,
        plateformes: selectedPlateformes,
        auto_sync_learndash: formData.auto_sync_learndash  // Utiliser la valeur du toggle
      };

      let response;
      if (id) {
        // Mise à jour
        response = await questionsAPI.update(id, formDataWithAssociations);
      } else {
        // Création
        response = await questionsAPI.create(formDataWithAssociations);
      }

      navigate('/questions');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la question:', error);
      setError('Erreur lors de la sauvegarde de la question');
    } finally {
      setLoading(false);
    }
  };

  // Function to sync the current question with LearnDash
  const handleSync = async () => {
    if (!id) {
      alert('Veuillez d\'abord sauvegarder la question avant de la synchroniser.');
      return;
    }

    setSyncing(true);
    try {
      const result = await learndashSyncAPI.syncQuestion(id);

      if (result.success) {
        alert(`Question synchronisée avec succès : ${result.action === 'created' ? 'créée' : 'mise à jour'} dans LearnDash`);
        // Recharger le statut de synchronisation pour mettre à jour l'affichage
        loadSyncStatus();
      } else {
        alert(`Erreur lors de la synchronisation : ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      alert(`Erreur lors de la synchronisation : ${error.message || 'Une erreur inconnue est survenue'}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading && id) {
    return (
      <div className="px-4 py-6 sm:px-0 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {id ? 'Modifier une question' : 'Créer une nouvelle question'}
            </h2>
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <span className="mr-3 text-sm font-medium text-orange-600">
                  Données dynamiques
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.donnees_dynamiques}
                    onChange={(e) => setFormData({ ...formData, donnees_dynamiques: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
              
              <div className="flex items-center">
                <span className="mr-3 text-sm font-medium text-green-600" title="Synchroniser automatiquement avec LearnDash à la création">
                  Sync LearnDash
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_sync_learndash}
                    onChange={(e) => setFormData({ ...formData, auto_sync_learndash: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section principale */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intitulé de la question *
                </label>
                <textarea
                  value={formData.intitule}
                  onChange={(e) => setFormData({ ...formData, intitule: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${duplicates.length > 0 ? 'border-yellow-400' : 'border-gray-300'}`}
                  rows="3"
                  required
                />
                {duplicates.length > 0 && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                    <p className="text-sm font-medium text-yellow-800 mb-1">
                      {duplicates.some(d => d.exact_match)
                        ? '⛔ Cette question existe déjà dans la base'
                        : `⚠️ ${duplicates.length} question(s) existante(s) contenant ce terme`}
                    </p>
                    <ul className="space-y-1">
                      {duplicates.map(d => (
                        <li key={d.id} className="text-sm text-yellow-700 flex items-start gap-1">
                          <span className="text-yellow-500 mt-0.5">•</span>
                          <a
                            href={`/questions/edit/${d.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline flex-1 line-clamp-2"
                            title={d.intitule}
                          >
                            {d.intitule.length > 120 ? d.intitule.slice(0, 120) + '…' : d.intitule}
                          </a>
                          <span className="text-xs text-yellow-500 whitespace-nowrap ml-1">{d.type_reponse}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Retour
                </label>
                <textarea
                  value={formData.feedback}
                  onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                />
              </div>
            </div>

            {/* Section des associations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Formations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formations *
                  <span className="ml-1 text-xs text-orange-600 font-normal" title="Au moins une formation est requise">
                    ⚠️
                  </span>
                </label>
                <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                  {formations.length > 0 ? (
                    formations.map((formation) => (
                      <div key={formation.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          id={`formation-${formation.id}`}
                          checked={selectedFormations.includes(formation.id)}
                          onChange={() => handleFormationChange(formation.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label
                          htmlFor={`formation-${formation.id}`}
                          className="ml-2 text-sm text-gray-700 truncate"
                          title={formation.nom}
                        >
                          {formation.nom}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Aucune formation disponible</p>
                  )}
                </div>
                <div className="flex items-center mt-1">
                  <button
                    type="button"
                    onClick={() => setShowFormationModal(true)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-medium py-1 px-2 rounded-md transition mr-1"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Modules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modules *
                  <span className="ml-1 text-xs text-orange-600 font-normal" title="Au moins un module est requis">
                    ⚠️
                  </span>
                </label>
                <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                  {domainesExpertise.length > 0 ? (
                    domainesExpertise.map((domaine) => (
                      <div key={domaine.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          id={`domaine-${domaine.id}`}
                          checked={selectedDomainesExpertise.includes(domaine.id)}
                          onChange={() => handleDomaineExpertiseChange(domaine.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label
                          htmlFor={`domaine-${domaine.id}`}
                          className="ml-2 text-sm text-gray-700 truncate"
                          title={domaine.titre}
                        >
                          {domaine.titre}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Aucun module disponible</p>
                  )}
                </div>
              </div>

              {/* Plateformes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plateformes à synchroniser
                  <span className="ml-1 text-xs text-blue-600 font-normal" title="Les questions seront synchronisées avec ces plateformes LearnDash">
                    ℹ️
                  </span>
                </label>
                <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
                  {plateformes.length > 0 ? (
                    plateformes.map((plateforme) => {
                      const isSynced = syncedPlatforms.includes(plateforme.wordpress_url);
                      const isSelected = selectedPlateformes.includes(plateforme.id);

                      return (
                        <div key={plateforme.id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            id={`plateforme-${plateforme.id}`}
                            checked={isSelected}
                            onChange={() => handlePlateformeChange(plateforme.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label
                            htmlFor={`plateforme-${plateforme.id}`}
                            className="ml-2 text-sm text-gray-700 truncate flex-1"
                            title={plateforme.nom}
                          >
                            {plateforme.nom}
                          </label>
                          {isSynced && (
                            <span className="text-xs text-green-600 font-medium ml-1" title="Déjà synchronisée">
                              ✓
                            </span>
                          )}
                          {!plateforme.wordpress_url && (
                            <span className="text-xs text-red-500 ml-1" title="Credentials WordPress non configurés">
                              ⚠️
                            </span>
                          )}
                          {!plateforme.is_active && (
                            <span className="text-xs text-gray-500 ml-1" title="Plateforme inactive">
                              (Inactive)
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500">Aucune plateforme disponible</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    Cochez les plateformes avec lesquelles synchroniser cette question
                  </p>
                  {syncedPlatforms.length > 0 && (
                    <p className="text-xs text-green-600 font-medium">
                      ✓ Synchronisée avec {syncedPlatforms.length} plateforme(s)
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section des réponses et Type de réponse */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Réponses possibles
                </label>
                <div className="w-full sm:w-48 mt-2 sm:mt-0">
                  <label className="block text-xs text-gray-500 mb-1 sm:text-right">
                    Type de réponse
                  </label>
                  <select
                    value={formData.type_reponse}
                    required
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData({ ...formData, type_reponse: newValue });

                      // Si le type est "vrai/faux", mettre automatiquement les réponses VRAI/FAUX
                      if (newValue === 'vrai/faux' && formData.reponses.length === 1 && formData.reponses[0].contenu === '') {
                        setFormData({
                          ...formData,
                          type_reponse: newValue,
                          reponses: [
                            { contenu: 'Vrai', est_correct: false },
                            { contenu: 'Faux', est_correct: false }
                          ]
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="QCM">QCM (Choix multiple)</option>
                    <option value="QCU">QCU (Choix unique)</option>
                    <option value="vrai/faux">Vrai/Faux</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                {formData.reponses.map((reponse, index) => (
                  <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 border border-gray-200 rounded-md">
                    <input
                      type="text"
                      value={reponse.contenu}
                      onChange={(e) => updateReponse(index, 'contenu', e.target.value)}
                      placeholder="Contenu de la réponse"
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={reponse.est_correct}
                        onChange={(e) => updateReponse(index, 'est_correct', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Correcte</span>
                    </div>
                    {formData.reponses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeReponse(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addReponse}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Ajouter une réponse
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition disabled:opacity-50"
              >
                {loading ? 'Enregistrement...' : (id ? 'Mettre à jour la question' : 'Créer la question')}
              </button>

              {id && !formData.auto_sync_learndash && (
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={syncing || loading}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-md transition disabled:opacity-50 flex items-center"
                >
                  {syncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Synchronisation...
                    </>
                  ) : (
                    'Synchroniser avec LearnDash'
                  )}
                </button>
              )}

              {id && formData.auto_sync_learndash && (
                <span className="text-sm text-green-600 font-medium flex items-center">
                  ✓ Synchronisation automatique activée
                </span>
              )}

              {/* Afficher le statut de synchronisation avec lien */}
              {id && syncedPlatforms.length > 0 && (
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-green-600">✓ Sync:</span> {syncedPlatforms.length} plateforme(s)
                  </div>
                  <button
                    type="button"
                    onClick={handleViewSyncDetails}
                    className="text-sm bg-green-50 hover:bg-green-100 text-green-700 font-medium py-1 px-3 rounded-md transition border border-green-200"
                    title="Voir les détails de synchronisation et les liens vers les plateformes"
                  >
                    🔗 Voir les liens
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => navigate('/questions')}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-6 rounded-md transition"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Afficher l'historique seulement en mode édition */}
      {id && (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <QuestionHistory questionId={id} />
        </div>
      )}

      <FormationModal
        show={showFormationModal}
        onClose={() => setShowFormationModal(false)}
        onCreate={createFormation}
        formData={newFormation}
        setFormData={setNewFormation}
      />
    </>
  );
}

// Composant principal
function CreateQuestion({ formations }) {
  const [localFormations, setLocalFormations] = useState(formations);
  const [localDomainesExpertise, setLocalDomainesExpertise] = useState([]);

  // Charger les modules
  useEffect(() => {
    loadDomainesExpertise();
  }, []);

  // Synchroniser les formations avec les props
  useEffect(() => {
    setLocalFormations(formations);
  }, [formations]);

  const loadDomainesExpertise = async () => {
    try {
      const domaines = await domainesExpertiseAPI.getAll();
      setLocalDomainesExpertise(domaines);
    } catch (error) {
      console.error('Erreur lors du chargement des domaines d\'expertise:', error);
    }
  };

  // Fonction pour ajouter une nouvelle formation à la liste locale
  const handleFormationCreated = (newFormation) => {
    // Mettre à jour l'état local
    setLocalFormations(prev => [...prev, newFormation]);
  };

  return (
    <CreateQuestionWithModals
      formations={localFormations}
      domainesExpertise={localDomainesExpertise}
      onFormationCreated={handleFormationCreated}
    />
  );
}

export default CreateQuestion;